/// <amd-module name="Environments/EnvironmentsCommon/Link" />
import { EnvironmentResourceType } from "DistributedTask/Client/Contracts/TaskAgent";
import { EnvironmentTabKeys } from "Environments/EnvironmentsCommon/Utils";

import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl, getAssetLocation } from "VSS/Platform/Location";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export function createUrlForRoute(pageContext: IVssPageContext, routeContributionId: string, queryParams: { [key: string]: string }): string {
    const curentRouteValues = getRouteValues(pageContext);

    let routeValues: { [key: string]: string } = { project: curentRouteValues.project, ...queryParams };
    if (curentRouteValues.team && !(queryParams["project"] && queryParams["project"] !== curentRouteValues.project)) {
        routeValues.team = curentRouteValues.team;
    }

    const constructedUrl = routeUrl(
        pageContext,
        routeContributionId,
        routeValues);

    return constructedUrl;
}

export function getResourceIdFromRoute(pageContext: IVssPageContext): number {
    let routeValues = getRouteValues(pageContext);
    return routeValues[resourceId] ? parseInt(routeValues[resourceId]) : -1;
}

export function getCurrentEnvironmentIdFromRoute(pageContext: IVssPageContext): number {
    return getRouteValueAsNumber(pageContext, "environmentId") || getRouteValueAsNumber(pageContext, "id");
}

export function getEnvironmentPageUrl(pageContext: IVssPageContext, environmentId: number, view?: string): string {
    view = view || EnvironmentTabKeys.EnvironmentDeploymentsTabKey;
    const queryParams = { environmentId: environmentId.toString(), view: view }
    return createUrlForRoute(pageContext, "ms.vss-environments-web.environments-route-with-id", queryParams);
}

export function getEnvironmentsSecurityPageUrl(pageContext: IVssPageContext, environmentId?: number): string {
    let resourceData: any = {};
    if(environmentId) {
        resourceData['environmentId'] = environmentId.toString();
    }

    return createUrlForRoute(pageContext, "ms.vss-environments-web.environments-security-default-route", resourceData);
}

export function getEnvironmentChecksPageUrl(pageContext: IVssPageContext, environmentId?: number): string {
    let resourceData: any = {};
    if(environmentId) {
        resourceData['environmentId'] = environmentId.toString();
    }

    return createUrlForRoute(pageContext, "ms.vss-environments-web.environment-checks-default-route", resourceData);
}

export function getEnvironmentsRunDetailsPageUrl(pageContext: IVssPageContext, environmentId: number, environmentExecutionRecordId: number, ownerId: number, definitionId: number, planType: string): string {
    return createUrlForRoute(pageContext, "ms.vss-environments-web.environment-rundetails-default-route", 
        {
            environmentId: environmentId.toString(),
            environmentExecutionRecordId: environmentExecutionRecordId.toString(),
            ownerId: ownerId.toString(),
            definitionId: definitionId.toString(),
            planType: planType,
            view: "jobshistory"
        });
}

export function getEnvironmentsPageUrl(pageContext: IVssPageContext): string {
    return createUrlForRoute(pageContext, "ms.vss-environments-web.environments-default-route", {});
}

export function getResourcePageUrl(pageContext: IVssPageContext, environmentId: number, resourceId: number, customRoute?: string): string {
    customRoute = customRoute ? customRoute : "ms.vss-environments-web.kubernetes-summary-default-route";
    return createUrlForRoute(pageContext, customRoute, { environmentId: environmentId.toString(), resourceId: resourceId.toString() });
}

export function getRouteValueAsNumber(pageContext: IVssPageContext, routeKey: string): number {
    let routeValue: string = getRouteValue(pageContext, routeKey);

    return parseInt(routeValue);
}

export function getRouteValue(pageContext: IVssPageContext, routeKey: string): any {
    let routeValues = getRouteValues(pageContext);

    if (!!routeValues) {
        return routeValues[routeKey];
    }

    return "";
}

export function getRouteValues(pageContext: IVssPageContext): any {
    return pageContext
        .getService<IVssNavigationService>("IVssNavigationService")
        .getCurrentRoute()
        .routeValues;
}

export function getResourceRoute(resourceType: EnvironmentResourceType): string {
    switch(resourceType) {
        case EnvironmentResourceType.Kubernetes:
            return "ms.vss-environments-web.kubernetes-summary-default-route";
        case EnvironmentResourceType.VirtualMachine:
            return "ms.vss-environments-web.virtualmachinegroups-summary-default-route";
        case EnvironmentResourceType.Generic:
        default:
            return "";
    }
}

export function getHistoryStateAsNumber(pageContext: IVssPageContext, key: string): number {
    const stateValue = getHistoryStateAsString(pageContext, key);

    return parseInt(stateValue);
}

export function getHistoryStateAsString(pageContext: IVssPageContext, key: string): string {
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    const state = historyService.getState();

    if (!!state && state[key]) {
        return state[key];
    }

    return "";
}

export const resourceId = "resourceId";